import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import api from "../api";
import "./Exceptions.css";

const fmt = n => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Exceptions() {
  const { user } = useAuth();
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loanInfo, setLoanInfo]     = useState(null);
  const [loadingLoan, setLoadingLoan] = useState(false);

  const [form, setForm] = useState({
    loan_id: "",
    exception_type: "rate_adjustment",
    amount_type: "dollars",
    amount: "",
    reason: ""
  });

  const load = () => {
    setLoading(true);
    api.get("/exceptions/").then(r => setExceptions(r.data)).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Fetch loan info when loan_id changes to calculate basis point preview
  useEffect(() => {
    if (form.loan_id && form.loan_id.length > 0) {
      setLoadingLoan(true);
      api.get(`/loans/${form.loan_id}`)
        .then(r => setLoanInfo(r.data))
        .catch(() => setLoanInfo(null))
        .finally(() => setLoadingLoan(false));
    } else {
      setLoanInfo(null);
    }
  }, [form.loan_id]);

  // Calculate dollar preview for basis points
  const bpDollarPreview = form.amount_type === "basis_points" && loanInfo && form.amount
    ? (loanInfo.loan_amount * (parseFloat(form.amount) / 10000)).toFixed(2)
    : null;

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const dollar_amount = form.amount_type === "basis_points" && loanInfo
        ? loanInfo.loan_amount * (parseFloat(form.amount) / 10000)
        : parseFloat(form.amount);

      await api.post("/exceptions/", {
        loan_id: parseInt(form.loan_id),
        exception_type: form.exception_type,
        amount_type: form.amount_type,
        amount: parseFloat(form.amount),
        dollar_amount: parseFloat(dollar_amount.toFixed(2)),
        reason: form.reason
      });
      setShowForm(false);
      setForm({ loan_id: "", exception_type: "rate_adjustment", amount_type: "dollars", amount: "", reason: "" });
      setLoanInfo(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Error creating exception");
    } finally { setSubmitting(false); }
  };

  const handleSignoff = async (id, decision) => {
    const comments = decision === "rejected" ? prompt("Reason for rejection:") : "";
    try {
      await api.post(`/exceptions/${id}/signoff`, { decision, comments });
      load();
    } catch (err) { alert(err.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="loading">Loading exceptions...</div>;

  return (
    <div className="exceptions-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Exceptions</h1>
          <p className="page-subtitle">{exceptions.length} total exceptions</p>
        </div>
        <button
          className="btn-secondary btn-sm"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? "✕ Cancel" : "+ New Exception"}
        </button>
      </div>

      {showForm && (
        <div className="exception-form-card card">
          <h2>Request Exception</h2>
          <form onSubmit={handleSubmit} className="exception-form">
            <div className="form-row-3">
              <div className="form-group">
                <label>Loan ID</label>
                <input
                  type="number"
                  value={form.loan_id}
                  onChange={e => setForm({...form, loan_id: e.target.value})}
                  required placeholder="e.g. 1"
                />
                {loanInfo && (
                  <span className="field-hint">
                    {loanInfo.borrower_last_name} — ${Number(loanInfo.loan_amount).toLocaleString()} {loanInfo.loan_type}
                  </span>
                )}
                {loadingLoan && <span className="field-hint">Looking up loan...</span>}
              </div>

              <div className="form-group">
                <label>Exception Type</label>
                <select value={form.exception_type} onChange={e => setForm({...form, exception_type: e.target.value})}>
                  <option value="rate_adjustment">Rate Adjustment</option>
                  <option value="bonus">Bonus</option>
                  <option value="deduction">Deduction</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Amount Type</label>
                <div className="amount-type-toggle">
                  <button
                    type="button"
                    className={`toggle-btn ${form.amount_type === "dollars" ? "active" : ""}`}
                    onClick={() => setForm({...form, amount_type: "dollars"})}
                  >
                    $ Dollars
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn ${form.amount_type === "basis_points" ? "active" : ""}`}
                    onClick={() => setForm({...form, amount_type: "basis_points"})}
                  >
                    bps Points
                  </button>
                </div>
              </div>
            </div>

            <div className="form-row-2">
              <div className="form-group">
                <label>
                  {form.amount_type === "dollars" ? "Amount ($)" : "Basis Points (bps)"}
                </label>
                <div className="amount-input-wrap">
                  <span className="amount-prefix">
                    {form.amount_type === "dollars" ? "$" : "bps"}
                  </span>
                  <input
                    type="number"
                    step={form.amount_type === "dollars" ? "0.01" : "1"}
                    min="0"
                    value={form.amount}
                    onChange={e => setForm({...form, amount: e.target.value})}
                    required
                    placeholder={form.amount_type === "dollars" ? "0.00" : "e.g. 25"}
                    className="amount-input"
                  />
                </div>
                {form.amount_type === "basis_points" && (
                  <span className="field-hint">
                    {bpDollarPreview
                      ? `≈ ${fmt(bpDollarPreview)} on this loan (${form.amount} bps × $${Number(loanInfo?.loan_amount).toLocaleString()})`
                      : "Enter a Loan ID above to see dollar preview"
                    }
                  </span>
                )}
                {form.amount_type === "dollars" && (
                  <span className="field-hint">Enter the dollar amount of the exception</span>
                )}
              </div>

              <div className="form-group">
                <label>Reason</label>
                <textarea
                  value={form.reason}
                  onChange={e => setForm({...form, reason: e.target.value})}
                  required
                  placeholder="Explain the reason for this exception..."
                  rows={3}
                />
              </div>
            </div>

            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit Exception Request"}
            </button>
          </form>
        </div>
      )}

      <div className="table-card">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Loan</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Dollar Value</th>
              <th>Reason</th>
              <th>Status</th>
              <th>Signoffs</th>
              {["manager","admin","executive"].includes(user?.role) && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {exceptions.length === 0 && (
              <tr><td colSpan={9} className="empty-row">No exceptions found</td></tr>
            )}
            {exceptions.map(ex => (
              <tr key={ex.id}>
                <td className="mono">#{ex.id}</td>
                <td className="mono">#{ex.loan_id}</td>
                <td>{ex.exception_type.replace("_", " ")}</td>
                <td className="mono">
                  {ex.amount_type === "basis_points"
                    ? <span className="bps-badge">{ex.amount} bps</span>
                    : fmt(ex.amount)
                  }
                </td>
                <td className="mono" style={{color:"var(--success)",fontWeight:600}}>{fmt(ex.dollar_amount)}</td>
                <td className="reason-cell">{ex.reason}</td>
                <td><span className={`badge badge-${ex.status}`}>{ex.status}</span></td>
                <td>{ex.signoffs?.length || 0}</td>
                {["manager","admin","executive"].includes(user?.role) && (
                  <td>
                    {ex.status === "pending" && (
                      <div className="action-btns">
                        <button className="btn-approve" onClick={() => handleSignoff(ex.id, "approved")}>Approve</button>
                        <button className="btn-reject"  onClick={() => handleSignoff(ex.id, "rejected")}>Reject</button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
